import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { colors } from '@/assets/styles/colors';
import { DateRangeFilter } from '@/components/ui/filters/DateRangeFilter';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import type { SimpleFilter } from '@/components/ui/filters/FilterDropdown';
import { Selector } from '@/components/ui/inputs/selector';
import { Text } from '@/components/ui/text/Text';
import type { SearchFilterConfig, UseAttendanceSearchFiltersResult } from '@/hooks/attendance/common/useAttendanceSearchFilters';


interface AttendanceSearchFiltersProps {
  config: SearchFilterConfig;
  state: UseAttendanceSearchFiltersResult;
}

export const AttendanceSearchFilters: React.FC<AttendanceSearchFiltersProps> = ({
  config,
  state,
}) => {
  const { t: translate } = useTranslation();
  const {
    filters,
    errors,
    eventOptions,
    markStatusOptions,
    isLoadingEvents,
    maxDate,
    maxRangeDays,
    handlers,
  } = state;

  const showFilterDropdown = config.events !== false || config.markStatus !== false;

  const markStatusFilter: SimpleFilter[] = useMemo(() => (
    config.markStatus !== false
      ? [{
        key: 'markStatus',
        label: translate('attendance:searchFilters.markStatus'),
        options: markStatusOptions,
        placeholder: translate('attendance:searchFilters.all'),
        supportingText: errors.markStatus,
      }]
      : []
  ), [config.markStatus, markStatusOptions, errors.markStatus, translate]);

  const onChangeMarkStatus = useCallback((key: string, value: string | string[]) => {
    if (key === 'markStatus') handlers.onMarkStatusChange(value);
  }, [handlers]);

  return (
    <div className="flex flex-wrap items-end gap-3">
      {config.dateRange !== false && (
        <div className="relative w-full sm:w-auto flex-shrink-0">
          <DateRangeFilter
            value={{ from: filters.dateFrom, to: filters.dateTo }}
            onChange={handlers.onDateRangeChange}
            closeOnComplete
            maxDate={maxDate}
            maxRangeDays={maxRangeDays}
          />
          {errors.dateRange && (
            <Text
              variant="small-regular"
              data-testid="error-message"
              color={colors.feedback.error300}
              className="absolute top-full left-0 mt-1"
            >
              {errors.dateRange}
            </Text>
          )}
        </div>
      )}

      {showFilterDropdown && (
        <FilterDropdown
          filters={markStatusFilter}
          values={{ markStatus: filters.markStatus }}
          onChangeFilter={onChangeMarkStatus}
          appendFilters={config.events !== false && (
            <div className="relative">
              <Selector
                label={translate('attendance:searchFilters.events')}
                value={filters.eventIds}
                options={eventOptions}
                onChange={handlers.onEventsChange}
                multiple
                searchable
                isLoading={isLoadingEvents}
                placeholder={translate('attendance:searchFilters.all')}
                color={errors.events ? 'error' : 'default'}
              />
              {errors.events && (
                <Text
                  variant="small-regular"
                  data-testid="error-message"
                  color={colors.feedback.error300}
                  className="absolute top-full left-0 mt-1"
                >
                  {errors.events}
                </Text>
              )}
            </div>
          )}
        />
      )}
    </div>
  );
};
